/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, FormControl, InputLabel, Select, MenuItem, Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventIcon from '@mui/icons-material/Event';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../../../api';
import { APPOINTMENT_DATE_RANGE_PRESETS, resolveAppointmentDateRange } from '../../../../utils/dateRangePresets';
import { STATUS_LIST, STATUS_LABELS, STATUS_HEX } from '../../../opdAppointments/constants';

const EMPTY_BY_STATUS = { SCHEDULED: 0, CHECKED_IN: 0, ATTENDED: 0, COMPLETED: 0, CANCELLED: 0 };
const TOTAL_HEX = '#00798E'; // brand teal — distinguishes the Total tile from the status colors

const STATUS_ICONS = {
  SCHEDULED: EventIcon,
  CHECKED_IN: HowToRegIcon,
  ATTENDED: MedicalServicesIcon,
  COMPLETED: CheckCircleIcon,
  CANCELLED: CancelIcon,
};

function StatTile({
  label, count, hex, Icon, loading, onClick, solid,
}) {
  return (
    <MDBox
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: 2,
        border: solid ? 'none' : '1px solid',
        borderColor: solid ? 'transparent' : alpha(hex, 0.22),
        bgcolor: solid ? hex : alpha(hex, 0.06),
        boxShadow: solid ? `0 4px 12px ${alpha(hex, 0.35)}` : 'none',
        px: 1.5,
        py: 1.25,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        '&:hover': solid ? {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 18px ${alpha(hex, 0.45)}`,
        } : {
          bgcolor: alpha(hex, 0.12),
          borderColor: alpha(hex, 0.45),
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
        },
      }}
    >
      <MDBox display="flex" alignItems="center" gap={0.75} minWidth={0}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={24}
          height={24}
          borderRadius="50%"
          flexShrink={0}
          sx={{ bgcolor: solid ? 'rgba(255,255,255,0.25)' : hex }}
        >
          <Icon sx={{ color: '#fff', fontSize: 14 }} />
        </MDBox>
        <MDTypography
          variant="caption"
          fontWeight="medium"
          sx={{
            color: solid ? 'rgba(255,255,255,0.9)' : '#7b809a',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            fontSize: '0.7rem',
            lineHeight: 1.2,
          }}
        >
          {label}
        </MDTypography>
      </MDBox>
      {loading ? (
        <Skeleton
          variant="text"
          width={36}
          height={30}
          sx={{ bgcolor: solid ? 'rgba(255,255,255,0.24)' : undefined }}
        />
      ) : (
        <MDTypography
          variant="h5"
          fontWeight="bold"
          sx={{ color: solid ? '#fff' : hex, lineHeight: 1.1 }}
        >
          {count}
        </MDTypography>
      )}
    </MDBox>
  );
}

export default function AppointmentStats() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [total, setTotal] = useState(0);
  const [byStatus, setByStatus] = useState(EMPTY_BY_STATUS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.contacts.getDoctorsList()
      .then((res) => setDoctors(res?.data || []))
      .catch(() => setDoctors([]));
  }, []);

  const range = resolveAppointmentDateRange(preset, { from: customFrom, to: customTo });

  const fetchStats = useCallback(async () => {
    if (!range.from || !range.to) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.appointment.getDashboardStats({
        date_from: range.from,
        date_to: range.to,
        doctor_id: doctorId || undefined,
      });
      setTotal(res?.data?.total || 0);
      setByStatus(res?.data?.by_status || EMPTY_BY_STATUS);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load appointment stats');
      setTotal(0);
      setByStatus(EMPTY_BY_STATUS);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, doctorId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const goToAppointments = (status) => {
    if (!range.from || !range.to) return;
    const params = new URLSearchParams({ date_from: range.from, date_to: range.to });
    if (doctorId) params.set('doctor_id', doctorId);
    if (status) params.set('status', status);
    navigate(`/opd-appointments?${params.toString()}`);
  };

  return (
    <Card sx={{ p: 2.5, height: '100%' }}>
      <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mb={2.5}>
        <MDTypography variant="h6" fontWeight="bold">Appointments</MDTypography>
        <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Date Range</InputLabel>
            <Select label="Date Range" value={preset} onChange={(e) => setPreset(e.target.value)}>
              {APPOINTMENT_DATE_RANGE_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {preset === 'custom' && (
            <>
              <MDDatePicker label="From" value={customFrom} onChange={(v) => setCustomFrom(v || '')} maxDate={customTo || undefined} />
              <MDDatePicker label="To" value={customTo} onChange={(v) => setCustomTo(v || '')} minDate={customFrom || undefined} />
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Doctor</InputLabel>
            <Select label="Doctor" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
              <MenuItem value="">All Doctors</MenuItem>
              {doctors.map((d) => (
                <MenuItem key={d.user_id} value={d.user_id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
      </MDBox>

      {error && (
        <MDTypography variant="caption" color="error" sx={{ display: 'block', mb: 1.5 }}>
          {error}
        </MDTypography>
      )}

      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 1.25,
        }}
      >
        <StatTile
          label="Total"
          count={total}
          hex={TOTAL_HEX}
          Icon={EventAvailableIcon}
          loading={loading}
          onClick={() => goToAppointments()}
          solid
        />
        {STATUS_LIST.map((status) => (
          <StatTile
            key={status}
            label={STATUS_LABELS[status]}
            count={byStatus[status] || 0}
            hex={STATUS_HEX[status]}
            Icon={STATUS_ICONS[status]}
            loading={loading}
            onClick={() => goToAppointments(status)}
          />
        ))}
      </MDBox>

      <MDTypography variant="caption" color="text" sx={{ display: 'block', mt: 1.5 }}>
        Click any card to view the filtered appointment list →
      </MDTypography>
    </Card>
  );
}
